import React, { useState, useEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, $getRoot, $createParagraphNode, $createTextNode } from 'lexical';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { $patchStyleText, $getSelectionStyleValueForProperty } from '@lexical/selection';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { mergeRegister } from '@lexical/utils';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';

// Lexical Editor Component
const LexicalTextEditor = ({
  content,
  onContentChange,
  fontSize = 14,
  fontFamily = "Roboto",
  placeholder = "",
  style = {},
  onKeyDown,
}) => {
  const initialConfig = {
    namespace: 'TextboxEditor',
    theme: {
      text: {
        bold: 'font-bold',
        italic: 'italic',
        underline: 'underline',
      },
      list: {
        nested: {
          listitem: 'list-item-nested',
        },
        ol: 'list-decimal list-inside',
        ul: 'list-disc list-inside',
        listitem: 'list-item',
      },
      link: 'text-blue-600 underline hover:text-blue-800',
    },
    onError: (error) => {
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      LinkNode,
    ],
  };

  const handleChange = (editorState) => {
    editorState.read(() => {
      const root = $getRoot();
      const textContent = root.getTextContent();
      const htmlContent = root.getTextContent(); // For now, we'll use text content
      onContentChange(htmlContent, textContent);
    });
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div style={{ position: 'relative', width: '100%' }}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              style={{
                width: '100%',
                height: '120px',
                padding: '12px',
                fontSize: `${fontSize}px`,
                fontFamily: fontFamily,
                border: '1px solid #ddd',
                borderRadius: '4px',
                overflowY: 'auto',
                outline: 'none',
                backgroundColor: 'white',
                lineHeight: '1.4',
                boxSizing: 'border-box',
                resize: 'none',
                ...style,
              }}
              placeholder={placeholder}
              onKeyDown={onKeyDown}
            />
          }
          placeholder={null}
          ErrorBoundary={LexicalErrorBoundary}
        />
        
        <OnChangePlugin onChange={handleChange} />
        <HistoryPlugin />
        <AutoFocusPlugin />
        <ListPlugin />
        <LinkPlugin />
        <TabIndentationPlugin />
      </div>
    </LexicalComposer>
  );
};

// Error boundary component
function LexicalErrorBoundary({ children }) {
  return (
    <div style={{ 
      border: '1px solid #ff6b6b', 
      borderRadius: '4px', 
      padding: '10px',
      backgroundColor: '#ffe6e6',
      color: '#cc0000'
    }}>
      {children}
    </div>
  );
}

// Formatting Toolbar Component
const FormattingToolbar = ({
  selectedColor = "#000000",
  handleColorChange,
  handleUnderlineChange,
  handleHighlightChange,
  handleNumberedList,
  handleBulletedList,
  handleLinkChange,
}) => {
  const [color, setColor] = useState("#000000");
  
  useEffect(() => {
    if (selectedColor) {
      setColor(selectedColor.startsWith("#") ? selectedColor : `#${selectedColor}`);
    }
  }, [selectedColor]);

  // Use global Lexical functions if available, otherwise fall back to DOM manipulation
  const handleColorClick = (newColor) => {
    if (window.handleLexicalColorChange) {
      window.handleLexicalColorChange(newColor);
    } else if (handleColorChange) {
      handleColorChange(newColor);
    }
  };

  const handleUnderlineClick = () => {
    if (window.handleLexicalUnderlineChange) {
      window.handleLexicalUnderlineChange();
    } else if (handleUnderlineChange) {
      handleUnderlineChange();
    }
  };

  const handleHighlightClick = () => {
    if (window.handleLexicalHighlightChange) {
      window.handleLexicalHighlightChange();
    } else if (handleHighlightChange) {
      handleHighlightChange();
    }
  };

  const handleNumberedListClick = () => {
    if (window.handleLexicalNumberedList) {
      window.handleLexicalNumberedList();
    } else if (handleNumberedList) {
      handleNumberedList();
    }
  };

  const handleBulletedListClick = () => {
    if (window.handleLexicalBulletedList) {
      window.handleLexicalBulletedList();
    } else if (handleBulletedList) {
      handleBulletedList();
    }
  };

  const handleLinkClick = () => {
    if (window.handleLexicalLinkChange) {
      window.handleLexicalLinkChange();
    } else if (handleLinkChange) {
      handleLinkChange();
    }
  };

  return (
    <div>
      {/* Icons and Color Picker Row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "6px 6px",
          marginBottom: "10px",
          gap: "30px",
          backgroundColor: "#f7f7f7",
          borderRadius: "5px",
        }}
      >
        {/* Link Icon */}
        <button
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
          }}
          onClick={handleLinkClick}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 10H5C3.61667 10 2.4375 9.5125 1.4625 8.5375C0.4875 7.5625 0 6.38333 0 5C0 3.61667 0.4875 2.4375 1.4625 1.4625C2.4375 0.4875 3.61667 0 5 0H9V2H5C4.16667 2 3.45833 2.29167 2.875 2.875C2.29167 3.45833 2 4.16667 2 5C2 5.83333 2.29167 6.54167 2.875 7.125C3.45833 7.70833 4.16667 8 5 8H9V10ZM6 6V4H14V6H6ZM11 10V8H15C15.8333 8 16.5417 7.70833 17.125 7.125C17.7083 6.54167 18 5.83333 18 5C18 4.16667 17.7083 3.45833 17.125 2.875C16.5417 2.29167 15.8333 2 15 2H11V0H15C16.3833 0 17.5625 0.4875 18.5375 1.4625C19.5125 2.4375 20 3.61667 20 5C20 6.38333 19.5125 7.5625 18.5375 8.5375C17.5625 9.5125 16.3833 10 15 10H11Z"
              fill="#0D6EFD"
            />
          </svg>
        </button>
        
        {/* Highlighter Icon */}
        <button
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
          }}
          onClick={handleHighlightClick}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.164062 16V13.7554H15.1282V16H0.164062ZM9.36849 7.12946L7.42315 5.18413L4.47335 8.13374C4.43021 8.17689 4.40863 8.22964 4.40863 8.29199C4.40863 8.35434 4.43021 8.40715 4.47335 8.45042L6.0906 10.0791C6.13387 10.1222 6.18668 10.1438 6.24903 10.1438C6.31138 10.1438 6.36413 10.1222 6.40728 10.0791L9.36849 7.12946ZM8.22298 4.39571L10.1569 6.32944L13.3512 3.14676C13.3943 3.10362 13.4159 3.04844 13.4159 2.98123C13.4159 2.91401 13.3943 2.85883 13.3512 2.81568L11.7367 1.20143C11.6936 1.15828 11.6385 1.13671 11.5714 1.13671C11.5042 1.13671 11.449 1.15828 11.4058 1.20143L8.22298 4.39571ZM7.04026 3.98999L10.5626 7.51217L7.2073 10.8791C6.93669 11.1496 6.61914 11.2848 6.25464 11.2848C5.89014 11.2848 5.57265 11.1496 5.30218 10.8791L5.23016 10.8071L4.54537 11.446H1.63896L3.76555 9.33087L3.70794 9.27345C3.43746 9.00284 3.29836 8.68149 3.29063 8.30938C3.28302 7.93715 3.41445 7.6158 3.68493 7.34532L7.04026 3.98999ZM7.04026 3.98999L10.6245 0.405715C10.895 0.135238 11.2125 0 11.577 0C11.9415 0 12.2591 0.135238 12.5297 0.405715L14.1583 2.02296C14.4288 2.29344 14.564 2.61093 14.564 2.97543C14.564 3.33993 14.4288 3.65742 14.1583 3.92789L10.5626 7.51217L7.04026 3.98999Z"
              fill="#6C757D"
            />
          </svg>
        </button>
        
        {/* Underline Icon */}
        <button
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
          }}
          onClick={handleUnderlineClick}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 13 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.195312 16V14.5639H12.9362V16H0.195312ZM6.56577 12.5569C5.07682 12.5569 3.91475 12.1049 3.07955 11.2009C2.2442 10.2969 1.82652 9.08572 1.82652 7.56724V0H3.60135V7.65557C3.60135 8.62288 3.8585 9.39808 4.37279 9.98115C4.88709 10.5642 5.61808 10.8558 6.56577 10.8558C7.51347 10.8558 8.24446 10.5642 8.75876 9.98115C9.27305 9.39808 9.5302 8.62288 9.5302 7.65557V0H11.305V7.56724C11.305 9.08572 10.8874 10.2969 10.052 11.2009C9.2168 12.1049 8.05473 12.5569 6.56577 12.5569Z"
              fill="#6C757D"
            />
          </svg>
        </button>
        
        {/* Bulleted List Icon */}
        <button
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
          }}
          onClick={handleBulletedListClick}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 17 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.66639 14.5766V13.1151H16.5712V14.5766H5.66639ZM5.66639 8.73073V7.26926H16.5712V8.73073H5.66639ZM5.66639 2.88484V1.42336H16.5712V2.88484H5.66639ZM1.62859 15.4667C1.18284 15.4667 0.801311 15.3079 0.484009 14.9905C0.166545 14.6732 0.0078125 14.2916 0.0078125 13.8459C0.0078125 13.4001 0.166545 13.0186 0.484009 12.7013C0.801311 12.3838 1.18284 12.2251 1.62859 12.2251C2.07434 12.2251 2.45586 12.3838 2.77316 12.7013C3.09063 13.0186 3.24936 13.4001 3.24936 13.8459C3.24936 14.2916 3.09063 14.6732 2.77316 14.9905C2.45586 15.3079 2.07434 15.4667 1.62859 15.4667ZM1.62859 9.62077C1.18284 9.62077 0.801311 9.46203 0.484009 9.14457C0.166545 8.82727 0.0078125 8.44574 0.0078125 7.99999C0.0078125 7.55424 0.166545 7.17272 0.484009 6.85541C0.801311 6.53795 1.18284 6.37922 1.62859 6.37922C2.07434 6.37922 2.45586 6.53795 2.77316 6.85541C3.09063 7.17272 3.24936 7.55424 3.24936 7.99999C3.24936 8.44574 3.09063 8.82727 2.77316 9.14457C2.45586 9.46203 2.07434 9.62077 1.62859 9.62077ZM1.62859 3.77487C1.18284 3.77487 0.801311 3.61614 0.484009 3.29868C0.166545 2.98137 0.0078125 2.59985 0.0078125 2.1541C0.0078125 1.70835 0.166545 1.32682 0.484009 1.00952C0.801311 0.692057 1.18284 0.533325 1.62859 0.533325C2.07434 0.533325 2.45586 0.692057 2.77316 1.00952C3.09063 1.32682 3.24936 1.70835 3.24936 2.1541C3.24936 2.59985 3.09063 2.98137 2.77316 3.29868C2.45586 3.61614 2.07434 3.77487 1.62859 3.77487Z"
              fill="#6C757D"
            />
          </svg>
        </button>
        
        {/* Numbered List Icon */}
        <button
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
          }}
          onClick={handleNumberedListClick}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 14 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.640625 15.4667V14.5296H2.60554V13.6982H1.42659V12.7612H2.60554V11.9298H0.640625V10.9927H2.87768C3.06605 10.9927 3.22396 11.0565 3.35142 11.1839C3.47887 11.3114 3.5426 11.4693 3.5426 11.6577V12.5949C3.5426 12.7833 3.47887 12.9412 3.35142 13.0687C3.22396 13.1961 3.06605 13.2599 2.87768 13.2599C3.06605 13.2599 3.22396 13.3236 3.35142 13.451C3.47887 13.5785 3.5426 13.7365 3.5426 13.925V14.8015C3.5426 14.99 3.47887 15.148 3.35142 15.2755C3.22396 15.4029 3.06605 15.4667 2.87768 15.4667H0.640625ZM0.640625 10.237V8.19648C0.640625 8.00811 0.704354 7.85013 0.831811 7.72255C0.959268 7.59509 1.11725 7.53136 1.30575 7.53136H2.60554V6.7002H0.640625V5.76294H2.87768C3.06605 5.76294 3.22396 5.82667 3.35142 5.95412C3.47887 6.08171 3.5426 6.23969 3.5426 6.42806V7.8035C3.5426 7.99187 3.47887 8.14985 3.35142 8.27744C3.22396 8.40489 3.06605 8.46862 2.87768 8.46862H1.57769V9.29978H3.5426V10.237H0.640625ZM1.81957 5.00723V1.47039H0.640625V0.533325H2.75664V5.00723H1.81957ZM5.20531 13.3053V12.1263H14.002V13.3053H5.20531ZM5.20531 8.58947V7.41052H14.002V8.58947H5.20531ZM5.20531 3.87368V2.69473H14.002V3.87368H5.20531Z"
              fill="#6C757D"
            />
          </svg>
        </button>
      </div>
      
      {/* Color Picker Row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "#f7f7f7",
          borderRadius: "5px",
          padding: "10px 5px 10px 5px",
          gap: "8px",
        }}
      >
        <span style={{ marginRight: "8px" }}>Color</span>
        <button
          style={{
            width: "20px",
            height: "20px",
            backgroundColor: "#FF0000",
            border: selectedColor === "#FF0000" ? "2px solid #666" : "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
          onClick={() => handleColorClick("#FF0000")}
        />
        <button
          style={{
            width: "20px",
            height: "20px",
            backgroundColor: "#FFA500",
            border: selectedColor === "#FFA500" ? "2px solid #666" : "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
          onClick={() => handleColorClick("#FFA500")}
        />
        <button
          style={{
            width: "20px",
            height: "20px",
            backgroundColor: "#000000",
            border: selectedColor === "#000000" ? "2px solid #666" : "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
          onClick={() => handleColorClick("#000000")}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span style={{ color: "#666" }}>Hex</span>
          <input
            type="color"
            value={color}
            onChange={(e) => {
              setColor(e.target.value);
              handleColorClick(e.target.value);
            }}
            style={{
              marginLeft: "4px",
              padding: "4px 8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              width: "80px",
            }}
          />
        </div>
      </div>
    </div>
  );
};

// Main TextboxContentFormatter component that provides the formatting toolbar
// This maintains compatibility with the existing TextBoxWithButtons component
const TextboxContentFormatter = ({
  handleColorChange,
  selectedColor = "#000000",
  handleUnderlineChange,
  handleHighlightChange,
  handleNumberedList,
  handleBulletedList,
  handleLinkChange,
}) => {
  return (
    <FormattingToolbar
      selectedColor={selectedColor}
      handleColorChange={handleColorChange}
      handleUnderlineChange={handleUnderlineChange}
      handleHighlightChange={handleHighlightChange}
      handleNumberedList={handleNumberedList}
      handleBulletedList={handleBulletedList}
      handleLinkChange={handleLinkChange}
    />
  );
};

export default TextboxContentFormatter;